import { useState } from "react";
import { CalendarDays, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDateOnly } from "@/lib/format";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const MINS = ["00", "15", "30", "45"];
const sel = "h-10 rounded-lg border border-input bg-card px-2 text-sm outline-none focus:border-primary";

// value: "YYYY-MM-DD" (date only) or "YYYY-MM-DDTHH:mm" (withTime, IST wall-clock)
export default function DatePicker({ value, onChange, testid, withTime }) {
  const [open, setOpen] = useState(false);
  const [d, t] = (value || "").split("T");
  const [hh, mm] = (t || "10:00").split(":");
  const h = Number(hh);
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = String(((h + 11) % 12) + 1);
  const mins = MINS.includes(mm) ? MINS : [...MINS, mm].sort();

  const emit = (nd, nh, nm, nap) => {
    if (!nd) return onChange("");
    const H = (Number(nh) % 12) + (nap === "PM" ? 12 : 0);
    onChange(withTime ? `${nd}T${pad(H)}:${nm}` : nd);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid} className="flex h-10 min-w-[140px] flex-1 items-center gap-2 rounded-lg border border-input bg-card px-3 text-left text-sm">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <span className={d ? "num" : "text-muted-foreground"}>{d ? fmtDateOnly(d) : "DD-MM-YYYY"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" weekStartsOn={1} selected={d ? new Date(`${d}T00:00:00`) : undefined}
            onSelect={(dt) => { emit(dt ? ymd(dt) : "", h12, mm, ap); setOpen(false); }} initialFocus />
        </PopoverContent>
      </Popover>
      {withTime && d && (
        <div className="flex gap-1">
          <select className={sel} value={h12} onChange={(e) => emit(d, e.target.value, mm, ap)} data-testid={`${testid}-hour`}>{HOURS.map((x) => <option key={x}>{x}</option>)}</select>
          <select className={sel} value={mm} onChange={(e) => emit(d, h12, e.target.value, ap)} data-testid={`${testid}-minute`}>{mins.map((x) => <option key={x}>{x}</option>)}</select>
          <select className={sel} value={ap} onChange={(e) => emit(d, h12, mm, e.target.value)} data-testid={`${testid}-ampm`}><option>AM</option><option>PM</option></select>
        </div>
      )}
      {d && (
        <button type="button" onClick={() => onChange("")} className="rounded-md p-2 text-muted-foreground hover:bg-secondary" data-testid={`${testid}-clear`} aria-label="Clear">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
